import * as THREE from 'three'
import { getNeighbors, getZoneColor } from '../../utils/board'
import {
  BOARD_POSITIONS,
  BOARD_TEXTURE_SIZE,
  boardToXZ,
  getBoardIrregularity,
  positionKey,
  worldLengthToCanvas,
  worldToCanvas,
} from './constants'
import type { MarblePalette } from './constants'

function seededRandom(seed: number) {
  let value = seed >>> 0 || 1
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0
    return value / 0xffffffff
  }
}

function withAlpha(hex: string, alpha: number) {
  return `${hex}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`
}

function makeCanvas(size: number) {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  return { canvas, ctx }
}

function forEachDimple(
  callback: (cx: number, cy: number, radius: number, rotation: number, ovalX: number, ovalZ: number) => void,
) {
  for (const pos of BOARD_POSITIONS) {
    const { x, z } = boardToXZ(pos)
    const irr = getBoardIrregularity(pos)
    const { x: cx, y: cy } = worldToCanvas(x, z)
    const radius = worldLengthToCanvas(irr.wellRadius)
    callback(cx, cy, radius, irr.rotation, irr.ovalX, irr.ovalZ)
  }
}

// Faint engraved grooves connecting neighbouring holes — reads as a hand-cut lattice.
function forEachGroove(callback: (ax: number, ay: number, bx: number, by: number) => void) {
  const present = new Set(BOARD_POSITIONS.map(positionKey))
  for (const pos of BOARD_POSITIONS) {
    const { x, z } = boardToXZ(pos)
    const a = worldToCanvas(x, z)
    for (const neighbor of getNeighbors(pos)) {
      const key = positionKey(neighbor)
      if (!present.has(key)) continue
      // Draw each edge once.
      if (neighbor.q < pos.q || (neighbor.q === pos.q && neighbor.r < pos.r)) continue
      const nb = boardToXZ(neighbor)
      const b = worldToCanvas(nb.x, nb.z)
      callback(a.x, a.y, b.x, b.y)
    }
  }
}

export interface BoardTextures {
  color: THREE.CanvasTexture
  roughness: THREE.CanvasTexture
  bump: THREE.CanvasTexture
}

// `bakedHoles: false` leaves the wood smooth (no baked holes/grooves) — used by
// the landing scene, which renders the holes as discrete geometry instead so
// they don't moiré into radial streaks at the grazing angle.
export function createBoardTextures(opts: { bakedHoles?: boolean } = {}): BoardTextures {
  const bakedHoles = opts.bakedHoles !== false
  const size = BOARD_TEXTURE_SIZE
  const color = makeCanvas(size)
  const rough = makeCanvas(size)
  const bump = makeCanvas(size)

  const rng = seededRandom(20240628)

  // --- Wood base tone -------------------------------------------------------
  const grad = color.ctx.createLinearGradient(size * 0.1, 0, size * 0.9, size)
  grad.addColorStop(0, '#7c4f2b')
  grad.addColorStop(0.32, '#653f22')
  grad.addColorStop(0.6, '#835434')
  grad.addColorStop(0.82, '#5d3a20')
  grad.addColorStop(1, '#6f4626')
  color.ctx.fillStyle = grad
  color.ctx.fillRect(0, 0, size, size)

  rough.ctx.fillStyle = '#d2d2d2' // wood ~0.82 roughness
  rough.ctx.fillRect(0, 0, size, size)
  bump.ctx.fillStyle = '#808080'
  bump.ctx.fillRect(0, 0, size, size)

  // Broad tonal blotches for depth.
  for (let i = 0; i < 30; i += 1) {
    const x = rng() * size
    const y = rng() * size
    const r = size * (0.08 + rng() * 0.22)
    const blotch = color.ctx.createRadialGradient(x, y, 0, x, y, r)
    const dark = rng() > 0.5
    blotch.addColorStop(0, dark ? 'rgba(46,26,12,0.14)' : 'rgba(190,140,86,0.12)')
    blotch.addColorStop(1, 'rgba(0,0,0,0)')
    color.ctx.fillStyle = blotch
    color.ctx.fillRect(0, 0, size, size)
  }

  // --- Flat-sawn wood grain -------------------------------------------------
  // Distant/decorative boards get coarser, softer, broader grain: thin fine
  // lines moiré into radial streaks at the grazing landing angle.
  const grainCount = bakedHoles ? 240 : 60
  const grainAlpha = bakedHoles ? 1 : 0.45
  const grainMinWidth = bakedHoles ? 0.6 : 2.4
  for (let i = 0; i < grainCount; i += 1) {
    const y = rng() * size
    const amp = 12 + rng() * 40
    const freq = 0.0016 + rng() * 0.0026
    const phase = rng() * Math.PI * 2
    const dark = rng() > 0.5
    color.ctx.beginPath()
    bump.ctx.beginPath()
    color.ctx.moveTo(-40, y)
    bump.ctx.moveTo(-40, y)
    for (let x = -40; x <= size + 40; x += 26) {
      const yy = y + Math.sin(x * freq + phase) * amp + Math.sin(x * freq * 3.1 + phase) * amp * 0.24
      color.ctx.lineTo(x, yy)
      bump.ctx.lineTo(x, yy)
    }
    color.ctx.strokeStyle = dark
      ? `rgba(44,24,11,${(0.1 + rng() * 0.16) * grainAlpha})`
      : `rgba(206,158,102,${(0.06 + rng() * 0.1) * grainAlpha})`
    color.ctx.lineWidth = grainMinWidth + rng() * 2.2
    color.ctx.stroke()
    bump.ctx.strokeStyle = dark ? 'rgba(70,70,70,0.28)' : 'rgba(180,180,180,0.2)'
    bump.ctx.lineWidth = 0.6 + rng() * 1.8
    bump.ctx.stroke()
  }

  // A couple of knots.
  for (let i = 0; i < 4; i += 1) {
    const x = size * (0.15 + rng() * 0.7)
    const y = size * (0.15 + rng() * 0.7)
    const rings = 4 + Math.floor(rng() * 4)
    for (let k = rings; k > 0; k -= 1) {
      const rr = (10 + k * (6 + rng() * 6))
      color.ctx.beginPath()
      color.ctx.ellipse(x, y, rr, rr * (0.6 + rng() * 0.3), rng() * Math.PI, 0, Math.PI * 2)
      color.ctx.strokeStyle = `rgba(38,20,9,${0.08 + rng() * 0.08})`
      color.ctx.lineWidth = 1 + rng() * 1.6
      color.ctx.stroke()
    }
  }

  // --- Engraved lattice + carved dimples ------------------------------------
  if (bakedHoles) forEachGroove((ax, ay, bx, by) => {
    color.ctx.strokeStyle = 'rgba(28,16,7,0.1)'
    color.ctx.lineWidth = worldLengthToCanvas(0.02)
    color.ctx.beginPath()
    color.ctx.moveTo(ax, ay)
    color.ctx.lineTo(bx, by)
    color.ctx.stroke()
    bump.ctx.strokeStyle = 'rgba(96,96,96,0.35)'
    bump.ctx.lineWidth = worldLengthToCanvas(0.02)
    bump.ctx.beginPath()
    bump.ctx.moveTo(ax, ay)
    bump.ctx.lineTo(bx, by)
    bump.ctx.stroke()
  })

  // --- Faint home-zone colour coding ----------------------------------------
  // A soft red wash over player 1's home triangle and blue over player 2's, so
  // each player can read which end of the board is theirs.
  const HOME_TINT: Record<string, string> = {
    player1: '156,28,36', // red (bottom)
    player2: '18,104,150', // blue (top)
  }
  for (const pos of BOARD_POSITIONS) {
    const zone = getZoneColor(pos)
    const rgb = zone ? HOME_TINT[zone] : undefined
    if (!rgb) continue
    const { x, z } = boardToXZ(pos)
    const { x: cx, y: cy } = worldToCanvas(x, z)
    const radius = worldLengthToCanvas(1.2)
    const wash = color.ctx.createRadialGradient(cx, cy, 0, cx, cy, radius)
    wash.addColorStop(0, `rgba(${rgb},0.22)`)
    wash.addColorStop(1, `rgba(${rgb},0)`)
    color.ctx.fillStyle = wash
    color.ctx.beginPath()
    color.ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    color.ctx.fill()
  }

  if (bakedHoles) forEachDimple((cx, cy, radius, rotation, ovalX, ovalZ) => {
    const drawOval = (ctx: CanvasRenderingContext2D, draw: () => void) => {
      ctx.save()
      ctx.translate(cx, cy)
      ctx.rotate(rotation)
      ctx.scale(ovalX, ovalZ)
      ctx.translate(-cx, -cy)
      draw()
      ctx.restore()
    }

    // Colour: dark recess with a light-catching rim on the upper-left.
    drawOval(color.ctx, () => {
      const g = color.ctx.createRadialGradient(cx, cy, 0, cx, cy, radius)
      g.addColorStop(0, 'rgba(26,15,7,0.92)')
      g.addColorStop(0.55, 'rgba(46,27,13,0.7)')
      g.addColorStop(0.86, 'rgba(70,44,22,0.32)')
      g.addColorStop(1, 'rgba(70,44,22,0)')
      color.ctx.fillStyle = g
      color.ctx.beginPath()
      color.ctx.arc(cx, cy, radius, 0, Math.PI * 2)
      color.ctx.fill()
      // rim highlight (upper-left) + soft cast (lower-right)
      color.ctx.beginPath()
      color.ctx.arc(cx, cy, radius * 0.94, Math.PI * 0.72, Math.PI * 1.5)
      color.ctx.strokeStyle = 'rgba(255,224,160,0.22)'
      color.ctx.lineWidth = radius * 0.12
      color.ctx.stroke()
      color.ctx.beginPath()
      color.ctx.arc(cx, cy, radius * 0.97, Math.PI * 1.72, Math.PI * 2.5)
      color.ctx.strokeStyle = 'rgba(18,9,4,0.28)'
      color.ctx.lineWidth = radius * 0.1
      color.ctx.stroke()
    })

    // Bump: concave (dark centre = recessed) with a slightly raised rim.
    drawOval(bump.ctx, () => {
      const g = bump.ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * 1.08)
      g.addColorStop(0, 'rgba(40,40,40,1)')
      g.addColorStop(0.7, 'rgba(120,120,120,0.9)')
      g.addColorStop(0.9, 'rgba(150,150,150,0.6)')
      g.addColorStop(1, 'rgba(128,128,128,0)')
      bump.ctx.fillStyle = g
      bump.ctx.beginPath()
      bump.ctx.arc(cx, cy, radius * 1.08, 0, Math.PI * 2)
      bump.ctx.fill()
    })

    // Roughness: the recess is a touch smoother/burnished.
    drawOval(rough.ctx, () => {
      const g = rough.ctx.createRadialGradient(cx, cy, 0, cx, cy, radius)
      g.addColorStop(0, 'rgba(150,150,150,0.6)')
      g.addColorStop(1, 'rgba(150,150,150,0)')
      rough.ctx.fillStyle = g
      rough.ctx.beginPath()
      rough.ctx.arc(cx, cy, radius, 0, Math.PI * 2)
      rough.ctx.fill()
    })
  })

  // --- Edge burnish (vignette) ---------------------------------------------
  const edge = color.ctx.createRadialGradient(size / 2, size / 2, size * 0.36, size / 2, size / 2, size * 0.5)
  edge.addColorStop(0, 'rgba(0,0,0,0)')
  edge.addColorStop(0.82, 'rgba(38,20,9,0.12)')
  edge.addColorStop(1, 'rgba(26,13,6,0.42)')
  color.ctx.fillStyle = edge
  color.ctx.fillRect(0, 0, size, size)

  const finish = (c: HTMLCanvasElement, srgb: boolean) => {
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
    tex.wrapS = THREE.ClampToEdgeWrapping
    tex.wrapT = THREE.ClampToEdgeWrapping
    // Trilinear mipmapping + max anisotropy so the fine grooves/grain don't
    // shimmer into radial moiré at the board's grazing viewing angle.
    tex.generateMipmaps = true
    tex.minFilter = THREE.LinearMipmapLinearFilter
    tex.magFilter = THREE.LinearFilter
    tex.anisotropy = 16
    return tex
  }

  return {
    color: finish(color.canvas, true),
    roughness: finish(rough.canvas, false),
    bump: finish(bump.canvas, false),
  }
}

// A single carved-hole sprite (dark recess + light-catching rim), drawn as a
// smooth radial gradient so it reads crisply as one hole with no periodic detail.
// Rendered per board position as instanced geometry, so the holes never form a
// baked grid that can moiré.
export function createHoleTexture(): THREE.CanvasTexture {
  const s = 128
  const { canvas, ctx } = makeCanvas(s)
  const c = s / 2
  const g = ctx.createRadialGradient(c, c, 0, c, c, c)
  g.addColorStop(0, 'rgba(20,11,5,0.95)')
  g.addColorStop(0.5, 'rgba(44,26,12,0.72)')
  g.addColorStop(0.78, 'rgba(70,44,22,0.34)')
  g.addColorStop(1, 'rgba(70,44,22,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(c, c, c, 0, Math.PI * 2)
  ctx.fill()
  // rim highlight (upper-left) + soft cast (lower-right)
  ctx.beginPath()
  ctx.arc(c, c, c * 0.8, Math.PI * 0.72, Math.PI * 1.5)
  ctx.strokeStyle = 'rgba(255,224,160,0.3)'
  ctx.lineWidth = c * 0.1
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(c, c, c * 0.84, Math.PI * 1.72, Math.PI * 2.5)
  ctx.strokeStyle = 'rgba(14,7,3,0.34)'
  ctx.lineWidth = c * 0.09
  ctx.stroke()

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

// Warm studio environment used purely for reflections on the glass marbles.
// Rendered through a PMREM generator elsewhere.
export function createEnvEquirect() {
  const w = 1024
  const h = 512
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  const sky = ctx.createLinearGradient(0, 0, 0, h)
  sky.addColorStop(0, '#fff4e0')
  sky.addColorStop(0.32, '#ecdcbc')
  sky.addColorStop(0.52, '#cdb892')
  sky.addColorStop(0.72, '#8a6440')
  sky.addColorStop(1, '#3c2716')
  ctx.fillStyle = sky
  ctx.fillRect(0, 0, w, h)

  // Warm key light (soft window) high on one side.
  const key = ctx.createRadialGradient(w * 0.3, h * 0.28, 0, w * 0.3, h * 0.28, h * 0.6)
  key.addColorStop(0, 'rgba(255,251,238,0.95)')
  key.addColorStop(0.4, 'rgba(255,240,210,0.4)')
  key.addColorStop(1, 'rgba(255,240,210,0)')
  ctx.fillStyle = key
  ctx.fillRect(0, 0, w, h)

  // Cooler fill on the opposite side for a little rim contrast.
  const fill = ctx.createRadialGradient(w * 0.78, h * 0.4, 0, w * 0.78, h * 0.4, h * 0.5)
  fill.addColorStop(0, 'rgba(210,232,255,0.3)')
  fill.addColorStop(1, 'rgba(210,232,255,0)')
  ctx.fillStyle = fill
  ctx.fillRect(0, 0, w, h)

  // A few soft horizontal softbox streaks that roll nicely across a sphere.
  for (const [y, alpha] of [[0.18, 0.35], [0.44, 0.18]] as const) {
    const streak = ctx.createLinearGradient(0, h * y - 12, 0, h * y + 12)
    streak.addColorStop(0, 'rgba(255,255,255,0)')
    streak.addColorStop(0.5, `rgba(255,252,244,${alpha})`)
    streak.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = streak
    ctx.fillRect(0, 0, w, h)
  }

  const tex = new THREE.CanvasTexture(canvas)
  tex.mapping = THREE.EquirectangularReflectionMapping
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// Soft warm tabletop the board rests on.
export function createBackdropTexture() {
  const size = 1024
  const { canvas, ctx } = makeCanvas(size)

  const base = ctx.createRadialGradient(size / 2, size * 0.46, size * 0.1, size / 2, size / 2, size * 0.62)
  base.addColorStop(0, '#d8c39a')
  base.addColorStop(0.55, '#c2a878')
  base.addColorStop(0.82, '#9c7f54')
  base.addColorStop(1, '#6d5334')
  ctx.fillStyle = base
  ctx.fillRect(0, 0, size, size)

  const rng = seededRandom(7)
  // Faint linen weave.
  for (let y = 0; y < size; y += 4) {
    ctx.fillStyle = y % 8 === 0 ? 'rgba(255,255,255,0.045)' : 'rgba(80,58,34,0.04)'
    ctx.fillRect(0, y + rng() * 1.4, size, 1)
  }
  for (let x = 0; x < size; x += 5) {
    ctx.fillStyle = x % 10 === 0 ? 'rgba(255,255,255,0.035)' : 'rgba(80,58,34,0.035)'
    ctx.fillRect(x + rng() * 1.4, 0, 1, size)
  }

  const vignette = ctx.createRadialGradient(size / 2, size / 2, size * 0.32, size / 2, size / 2, size * 0.52)
  vignette.addColorStop(0, 'rgba(0,0,0,0)')
  vignette.addColorStop(1, 'rgba(28,16,7,0.5)')
  ctx.fillStyle = vignette
  ctx.fillRect(0, 0, size, size)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

// Opaque swirl core suspended inside the glass marble — the classic "cat's eye".
export function createMarbleCoreTexture(palette: MarblePalette, seed: number) {
  const size = 256
  const { canvas, ctx } = makeCanvas(size)
  const rng = seededRandom(seed)

  const base = ctx.createRadialGradient(size * 0.4, size * 0.36, 6, size / 2, size / 2, size * 0.62)
  base.addColorStop(0, palette.coreLight)
  base.addColorStop(0.3, palette.core)
  base.addColorStop(0.72, palette.base)
  base.addColorStop(1, palette.dark)
  ctx.fillStyle = base
  ctx.fillRect(0, 0, size, size)

  // Cat's-eye ribbon.
  ctx.save()
  ctx.lineCap = 'round'
  ctx.translate(size / 2, size / 2)
  ctx.rotate((rng() - 0.5) * Math.PI)
  ctx.translate(-size / 2, -size / 2)
  for (const [width, color] of [[46, withAlpha(palette.core, 0.5)], [26, withAlpha(palette.coreLight, 0.85)], [10, 'rgba(255,255,255,0.6)']] as const) {
    ctx.beginPath()
    ctx.moveTo(-20, size * 0.5)
    for (let x = -20; x <= size + 20; x += 24) {
      ctx.lineTo(x, size * 0.5 + Math.sin(x * 0.03 + seed) * 26)
    }
    ctx.strokeStyle = color
    ctx.lineWidth = width
    ctx.stroke()
  }
  ctx.restore()

  // Wispy veins + flecks.
  for (let i = 0; i < 14; i += 1) {
    ctx.beginPath()
    const y = rng() * size
    ctx.moveTo(-10, y)
    for (let x = -10; x <= size + 10; x += 30) {
      ctx.lineTo(x, y + Math.sin(x * 0.05 + rng() * 4) * (8 + rng() * 14))
    }
    ctx.strokeStyle = rng() > 0.5 ? withAlpha(palette.vein, 0.24) : 'rgba(255,255,255,0.1)'
    ctx.lineWidth = 0.8 + rng() * 1.8
    ctx.stroke()
  }

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}
